import { useCallback, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { CountryDropdownButton, CountryDropdownItem, Dropdown } from "@similarweb/ui-components";

import { abbrNumberFilter, percentageFilter } from "app/filters/ngFilters";
import CountryService from "app/services/CountryService";

import type { GscCountry } from "../../../types";
import { ErrorMessage } from "../../styles";
import { dropdownTextSearchFilter } from "../../utils";

import { ClicksValue, HeaderRow, LocationPickerWrapper, PercentageValue, ValueRow } from "./styles";
import type { EnrichedCountry } from "./types";

type Props = {
    countries: GscCountry[];
    hiddenCountryCodes?: string[];
    selectedCountryCode: string | undefined;
    onSelect: (countryCode: string) => void;
    isLoading: boolean;
    isUnsupportedCountry?: boolean;
};

export const LocationPicker = ({
    countries,
    hiddenCountryCodes,
    selectedCountryCode,
    onSelect,
    isLoading = false,
    isUnsupportedCountry = false,
}: Props) => {
    const { translate } = useTranslate();
    const [isOpen, setIsOpen] = useState(false);
    const [isTouched, setIsTouched] = useState(false);

    const handleToggle = useCallback(
        (open: boolean) => {
            setIsOpen(open);
            if (!open && !selectedCountryCode) {
                setIsTouched(true);
            }
        },
        [selectedCountryCode],
    );

    const isEmptyError = isTouched && !selectedCountryCode;
    const showError = isEmptyError || isUnsupportedCountry;
    let errorMessage: string | undefined;
    if (isEmptyError) {
        errorMessage = translate("rankTracker.gsc.locationPicker.error.empty");
    } else if (isUnsupportedCountry) {
        errorMessage = translate("rankTracker.gsc.locationPicker.error.unsupportedCountry");
    }

    const totalClicks = useMemo(() => {
        return countries.reduce((sum, country) => sum + country.clicks, 0);
    }, [countries]);

    const hiddenSet = useMemo(() => new Set(hiddenCountryCodes), [hiddenCountryCodes]);

    // Countries not found in SimilarWeb's CountryService are filtered out (GSC may return codes SW doesn't support).
    // Countries in hiddenCountryCodes are also excluded (all device slots are taken).
    const enrichedCountries = useMemo(() => {
        return countries
            .map((country): EnrichedCountry | null => {
                if (hiddenSet.has(country.country)) {
                    return null;
                }
                const countryData = CountryService.getCountryByCode(country.country);
                if (!countryData) {
                    return null;
                }
                return {
                    ...country,
                    id: countryData.id,
                    name: countryData.text,
                    percentage: totalClicks > 0 ? country.clicks / totalClicks : 0,
                };
            })
            .filter((country): country is EnrichedCountry => country !== null)
            .sort((a, b) => b.clicks - a.clicks);
    }, [countries, hiddenSet, totalClicks]);

    const selectedCountry = enrichedCountries.find((c) => c.country === selectedCountryCode);

    const handleCountrySelect = useCallback(
        (item: { id: number }) => {
            const country = enrichedCountries.find((c) => c.id === item.id);
            if (country) {
                onSelect(country.country);
            }
        },
        [enrichedCountries, onSelect],
    );

    const placeholder = translate("rankTracker.gsc.locationPicker.searchPlaceholder");

    return (
        <LocationPickerWrapper
            data-automation="gsc-location-picker"
            $hasValue={!!selectedCountry}
        >
            <Dropdown
                isOpen={isOpen}
                onToggle={handleToggle}
                onClick={handleCountrySelect}
                closeOnItemClick
                appendTo="body"
                hasSearch
                width="400px"
                dropdownPopupPlacement="ontop-left"
                searchPlaceHolder={placeholder}
                searchFilter={dropdownTextSearchFilter}
            >
                {[
                    <CountryDropdownButton
                        key="country-dropdown-button"
                        countryId={selectedCountry?.id}
                        height={40}
                        error={showError}
                        disabled={isLoading}
                    >
                        {selectedCountry?.name ?? placeholder}
                    </CountryDropdownButton>,
                    <HeaderRow key="header-row">
                        <span>{translate("rankTracker.gsc.locationPicker.headerCountry")}</span>
                        <span>{translate("rankTracker.gsc.locationPicker.headerRight")}</span>
                    </HeaderRow>,
                    ...enrichedCountries.map((country) => (
                        <CountryDropdownItem
                            key={country.country}
                            id={country.id}
                            text={country.name}
                            data={
                                <ValueRow>
                                    <ClicksValue>{abbrNumberFilter()(country.clicks)}</ClicksValue>
                                    <PercentageValue>{percentageFilter()(country.percentage, 1)}%</PercentageValue>
                                </ValueRow>
                            }
                        />
                    )),
                ]}
            </Dropdown>
            {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}
        </LocationPickerWrapper>
    );
};
